import { Fragment, useState, type ReactNode } from "react";
import { CONCEPT_ICONS, Icons } from "@/components/ui";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  type ContextMenuAnchor,
} from "@/components/ui/context-menu";
import { ContextMenuSubmenuItem } from "@/components/ui/context-menu-submenu";
import { useRevealLabel } from "@/components/shapes/file-frame/use-file-actions";
import type { FrameChromeMenuItem } from "@/components/shapes/shared/frame-chrome-config";
import type { FrameDownloadOption } from "@/components/shapes/shared/frame-download";
import { FrameMoveToProjectDialog } from "@/components/shapes/shared/frame-move-to-project-dialog";
import type { WidgetSize } from "@/components/shapes/shared/widget-face";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { commonMessages } from "@/i18n/messages/common";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { panelsSearchMessages } from "@/i18n/messages/panels-search";
import { wallMessages } from "@/i18n/messages/wall";
import { moveFramesToProject } from "@/lib/frame-menu-actions";
import type { FrameMenuGroups, FrameMenuItemId } from "@/lib/frame-menu-model";
import { isMacPlatform } from "@/lib/os-platform";
import { WallCardMenuSizes } from "./wall-card-menu-sizes";
import { WallDeleteConfirm } from "./wall-delete-confirm";

/**
 * What the host runs; Download, Move to project and a desktop widget's
 * size row are this menu's own.
 */
export type WallCardMenuAction = Exclude<
  FrameMenuItemId,
  "download" | "moveToProject" | "size"
>;

/** The items drawn as one menu row each. */
type RowItemId = Exclude<FrameMenuItemId, "download" | "size">;

/** One card (a right-click, ⋯) or a multi-selection, and where it opened. */
export interface WallCardMenuTarget {
  frameId: string;
  anchor: ContextMenuAnchor;
}

export interface FrameMenuTarget {
  frameIds: readonly string[];
  anchor: ContextMenuAnchor;
}

interface WallCardMenuProps {
  /**
   * The items whose menu is open; `null` closes it. Stay mounted with `null`:
   * Delete's confirmation and the project picker outlive the menu.
   */
  target: FrameMenuTarget | null;
  /** `frameMenuGroups(…)` for the target. */
  groups: FrameMenuGroups;
  /**
   * An opened item's type extras (Copy link, Fit globe) join the first
   * group; a destructive one (Discard branch) joins Delete's.
   */
  extras?: readonly FrameChromeMenuItem[];
  /** A right-click on selected text: Copy selection leads the menu. */
  onCopySelection?: () => void;
  /**
   * A full frame's formats: past one, Download is a choice. `null` (a
   * card-only frame): Download… saves its one format once it loads.
   */
  downloadOptions?: readonly FrameDownloadOption[] | null;
  onDownload?: (option: FrameDownloadOption | undefined) => void;
  onClose: () => void;
  /** Delete reaches here only after its confirmation. */
  onAction: (frameIds: readonly string[], action: WallCardMenuAction) => void;
  /** A desktop widget's size, the sizes its type takes and its change, for its size row. */
  widgetSize?: WidgetSize | null;
  widgetSizes?: readonly WidgetSize[];
  onWidgetSize?: (size: WidgetSize) => void;
}

const ITEM_ICON: Record<RowItemId, ReactNode> = {
  unarchive: <Icons.RotateCcw />,
  open: <Icons.Maximize />,
  focus: <Icons.Scan />,
  openTask: <CONCEPT_ICONS.task />,
  copy: <Icons.Copy />,
  showFile: <Icons.FolderOpen />,
  pin: <Icons.Bookmark />,
  unpin: <Icons.Bookmark />,
  pinToDesktop: <Icons.Monitor />,
  unpinFromDesktop: <Icons.Monitor />,
  moveToProject: <CONCEPT_ICONS.project />,
  archive: <Icons.Archive />,
  delete: <Icons.Trash2 />,
};

const COPY_SHORTCUT = isMacPlatform() ? "⌘C" : "Ctrl C";

/**
 * A wall card's menu, a multi-selection's, an opened item's ⋯ and
 * right-click and a desktop widget's ⋯: the model's groups in order,
 * separated, the type's extras in place. An empty menu does not open.
 */
export function WallCardMenu({
  target,
  groups,
  extras = [],
  onCopySelection,
  downloadOptions = null,
  onDownload,
  onClose,
  onAction,
  widgetSize = null,
  widgetSizes = [],
  onWidgetSize,
}: WallCardMenuProps) {
  // The menu keeps its last items while it fades out.
  const [shown, setShown] = useState(target);
  if (target && target !== shown) setShown(target);
  const [deleting, setDeleting] = useState<readonly string[] | null>(null);
  const [moving, setMoving] = useState<readonly string[] | null>(null);
  const frameIds = shown?.frameIds ?? [];
  const several = frameIds.length > 1;
  const t = useT(wallMessages);
  const tCanvas = useT(canvasMessages);
  const tCommon = useT(commonMessages);
  const tFrames = useT(framesSharedMessages);
  const tSearch = useT(panelsSearchMessages);
  const revealLabel = useRevealLabel();
  const labels: Record<RowItemId, string> = {
    unarchive: t("unarchive"),
    open: tCommon("open"),
    focus: several ? tCanvas("focusTheseWindows") : t("focus"),
    openTask: tFrames("openTask"),
    copy: tCommon("copy"),
    showFile: revealLabel,
    pin: t("bookmark"),
    unpin: t("removeBookmark"),
    pinToDesktop: t("pinToDesktop"),
    unpinFromDesktop: t("unpinFromDesktop"),
    moveToProject: tFrames("moveToProjectMenu"),
    archive: tSearch("archive"),
    delete: tCommon("delete"),
  };

  function renderExtra(item: FrameChromeMenuItem) {
    return (
      <ContextMenuItem
        key={`extra-${item.id}`}
        icon={item.icon ? <item.icon /> : undefined}
        destructive={item.destructive}
        onClick={item.onSelect}
        data-testid={`wall-card-menu-extra-${item.id}`}
      >
        {item.label}
      </ContextMenuItem>
    );
  }

  function renderDownload() {
    if (downloadOptions && downloadOptions.length > 1) {
      return (
        <ContextMenuSubmenuItem
          key="download"
          label={t("download")}
          icon={<Icons.Download />}
          menuLabel={t("download")}
          flyoutClassName="min-w-[calc(var(--appkit-space-xl)*5)]"
          data-testid="wall-card-menu-download"
        >
          {downloadOptions.map((option) => (
            <ContextMenuItem
              key={option.label}
              icon={<Icons.Download />}
              onClick={() => onDownload?.(option)}
              data-testid={`wall-card-menu-download-${option.extension}`}
            >
              {option.label}
            </ContextMenuItem>
          ))}
        </ContextMenuSubmenuItem>
      );
    }
    return (
      <ContextMenuItem
        key="download"
        icon={<Icons.Download />}
        onClick={() => onDownload?.(downloadOptions?.[0])}
        data-testid="wall-card-menu-download"
      >
        {t("downloadItem")}
      </ContextMenuItem>
    );
  }

  function renderItem(item: FrameMenuItemId) {
    if (item === "download") return renderDownload();
    if (item === "size") {
      return (
        <WallCardMenuSizes
          key="size"
          current={widgetSize}
          sizes={widgetSizes}
          onPick={(size) => onWidgetSize?.(size)}
        />
      );
    }
    return (
      <ContextMenuItem
        key={item}
        icon={ITEM_ICON[item]}
        destructive={item === "delete"}
        onClick={() => {
          if (item === "delete") setDeleting(frameIds);
          else if (item === "moveToProject") setMoving(frameIds);
          else onAction(frameIds, item);
        }}
        data-testid={`wall-card-menu-${item}`}
      >
        {labels[item]}
      </ContextMenuItem>
    );
  }

  const sections: ReactNode[][] = [
    groups.size.map(renderItem),
    onCopySelection
      ? [
          <ContextMenuItem
            key="copySelection"
            icon={<Icons.Copy />}
            trailing={<span className="text-muted">{COPY_SHORTCUT}</span>}
            onClick={onCopySelection}
            data-testid="wall-card-menu-copySelection"
          >
            {tFrames("copySelection")}
          </ContextMenuItem>,
        ]
      : [],
    groups.restore.map(renderItem),
    [
      ...groups.go.map(renderItem),
      ...extras.filter((item) => !item.destructive).map(renderExtra),
    ],
    groups.content.map(renderItem),
    groups.place.map(renderItem),
    [
      ...extras.filter((item) => item.destructive).map(renderExtra),
      ...groups.end.map(renderItem),
    ],
  ].filter((section) => section.length > 0);

  return (
    <>
      <ContextMenu anchor={target?.anchor ?? null} onClose={onClose}>
        {shown !== null && sections.length > 0 && (
          <ContextMenuContent>
            {sections.map((section, index) => (
              <Fragment key={index}>
                {index > 0 && <ContextMenuSeparator />}
                {section}
              </Fragment>
            ))}
          </ContextMenuContent>
        )}
      </ContextMenu>
      {deleting !== null && (
        <WallDeleteConfirm
          frameIds={deleting}
          onConfirm={() => {
            setDeleting(null);
            onAction(deleting, "delete");
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
      {moving !== null && (
        <FrameMoveToProjectDialog
          frameIds={moving}
          onMove={(projectId) => moveFramesToProject(moving, projectId)}
          onClose={() => setMoving(null)}
        />
      )}
    </>
  );
}
